import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import FeedbackWidget from "@/components/FeedbackWidget";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import ReferralCard from "@/components/ReferralCard";
import SmsPromptCard from "@/components/SmsPromptCard";
import TrialBanner from "@/components/TrialBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Heart, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [chests, setChests] = useState({ owned: [], shared: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", relationship: "", dob: "", conditions: "", allergies: "", blood_type: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hope-chests");
      setChests(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Please give this Hope Chest a name"); return; }
    try {
      await api.post("/hope-chests", form);
      toast.success("Hope Chest created");
      setOpen(false);
      setForm({ name: "", relationship: "", dob: "", conditions: "", allergies: "", blood_type: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <AppShell>
      <FeedbackWidget />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2" data-testid="dashboard-greeting">
              {greeting}, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-[#7A736E] mt-2">Here are the people you're caring for.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] py-6 px-6" data-testid="dashboard-new-chest-button">
                <Plus className="w-4 h-4 mr-2" /> New Hope Chest
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Create a new Hope Chest</DialogTitle>
                <DialogDescription>Start gently — add the basics now, more details later.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Their name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mom, Dad, Eleanor" className="rounded-xl mt-1.5" data-testid="chest-form-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Relationship</Label>
                    <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Mother, Father…" className="rounded-xl mt-1.5" data-testid="chest-form-relationship" />
                  </div>
                  <div>
                    <Label>Date of birth</Label>
                    <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="rounded-xl mt-1.5" data-testid="chest-form-dob" />
                  </div>
                </div>
                <div>
                  <Label>Blood type</Label>
                  <Input value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} placeholder="A+, O-, …" className="rounded-xl mt-1.5" data-testid="chest-form-blood" />
                </div>
                <div>
                  <Label>Conditions</Label>
                  <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Diabetes, hypertension…" className="rounded-xl mt-1.5" />
                </div>
                <div>
                  <Label>Allergies</Label>
                  <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="Penicillin, peanuts…" className="rounded-xl mt-1.5" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="chest-form-submit">Create Hope Chest</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-[#7A736E]">Loading…</p>
        ) : (chests.owned.length + chests.shared.length === 0) ? (
          <>
            <TrialBanner user={user} />
            <div className="hc-card p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#D8E2D8] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-[#3A5A4A]" />
            </div>
            <h2 className="font-display text-2xl mb-2">Let's create your first Hope Chest.</h2>
            <p className="text-[#7A736E] max-w-md mx-auto mb-6">Start with the basics — a name, a relationship. You can add medications, appointments and documents as you go.</p>
            <Button onClick={() => setOpen(true)} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="empty-create-chest-button">
              <Plus className="w-4 h-4 mr-2" /> Create my first Hope Chest
            </Button>
          </div>
          </>
        ) : (
          <>
            <TrialBanner user={user} />
            <OnboardingChecklist chests={chests} />
            <SmsPromptCard user={user} />
            <ReferralCard />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...chests.owned, ...chests.shared].map((c) => (
                <Link
                  key={c.id}
                  to={`/chest/${c.id}`}
                  className="hc-card hc-card-interactive p-7 text-left group block"
                  data-testid={`chest-card-${c.id}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-[#F3EFEA] flex items-center justify-center">
                      <Heart className="w-6 h-6 text-[#C87B67]" fill="#C87B67" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#7A736E] group-hover:text-[#3A5A4A] group-hover:translate-x-1 transition" />
                  </div>
                  <h3 className="font-display text-xl font-semibold">{c.name}</h3>
                  <p className="text-sm text-[#7A736E] mt-1">{c.relationship || "—"}</p>
                  {c.conditions && <p className="text-xs text-[#7A736E] mt-3 line-clamp-2">{c.conditions}</p>}
                </Link>
              ))}
            </div>
            {user?.subscription_tier === "free" && chests.owned.length >= 1 && (
              <div className="mt-12 p-8 rounded-2xl bg-[#F3EFEA] border border-[#EBE5DF] flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold">Caring for more than one loved one?</h3>
                  <p className="text-sm text-[#7A736E] mt-1">Upgrade to Premium for unlimited Hope Chests, family sharing, and advanced reports.</p>
                </div>
                <Button onClick={() => nav("/pricing")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="upgrade-cta-button">See Premium</Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
